import { styled } from '@linaria/react';

import ArrowChar from '@/components/ArrowChar';

import {
  formatForCss,
  formatWithSyntaxHighlighting,
  checkIfTwoColorsAreIdentical,
} from './ColorfulParticlesDemo.helpers';
import {
  Variant,
  SampledParticleColors,
} from './ColorfulParticlesDemo.types';

interface Props {
  colors: Array<SampledParticleColors>;
  variant: Variant;
}

function ColorTable({ colors, variant }: Props) {
  const onlyIncludesOneColor = colors.every(
    ({ fromColor, toColor }) =>
      checkIfTwoColorsAreIdentical(fromColor, toColor)
  );

  return (
    <Wrapper
      style={{
        minHeight:
          variant === 'hsl-plus-180' ? '26.625rem' : '16.3125rem',
      }}
    >
      <Heading>Sample Colors</Heading>
      {colors.length === 0 && (
        <EmptyParagraph>
          (<Mouse>Click</Mouse>
          <Touch>Tap</Touch> the heart to generate some particles. The
          first few colors will be shown here.)
        </EmptyParagraph>
      )}
      <List data-columns={onlyIncludesOneColor ? '2' : '1'}>
        {colors.map(({ fromColor, toColor }, index) => {
          if (onlyIncludesOneColor) {
            const formattedColor =
              formatWithSyntaxHighlighting(fromColor);

            return (
              <ListItem
                key={fromColor.id}
                style={{ '--index': index }}
              >
                <Value>
                  <Box
                    style={{ background: formatForCss(fromColor) }}
                  />
                  <Label>{formattedColor}</Label>
                </Value>
              </ListItem>
            );
          } else {
            return (
              <DualListItem
                key={fromColor.id}
                style={{ '--index': index }}
              >
                <Value>
                  <Box
                    style={{ background: formatForCss(fromColor) }}
                  />
                  <Label>
                    {formatWithSyntaxHighlighting(fromColor)}
                  </Label>
                </Value>
                <ArrowChar direction="right" />
                <Value>
                  <Box
                    style={{ background: formatForCss(toColor) }}
                  />
                  <Label>
                    {formatWithSyntaxHighlighting(toColor)}
                  </Label>
                </Value>
              </DualListItem>
            );
          }
        })}
      </List>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  font-family: var(--font-family-mono);
  border: 1px solid var(--color-gray-300);
  padding: 1rem;
  border-radius: 8px;
  container-type: inline-size;

  @media (max-width: 30rem) {
    padding: 0.5rem;
  }
`;

const Heading = styled.p`
  text-align: center;
  font-size: 1rem;
  margin-bottom: 1.5em;
  font-weight: var(--font-weight-medium);
  text-transform: uppercase;
`;

const EmptyParagraph = styled.p`
  font-style: italic;
  font-size: 1rem;
  color: var(--color-gray-700);
  text-align: center;
  text-wrap: balance;
`;

const List = styled.ul`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  list-style: none;
  padding: 0;
  gap: 4px;

  @media (min-width: 35rem) {
    &[data-columns='2'] {
      grid-template-columns: 1fr 1fr;
    }
  }
`;

const ListItem = styled.li`
  width: 100%;
  background: var(--color-gray-100);
  padding: 0.5rem 1rem;
  border-radius: 2px;
  animation: fadeFromTransparent 500ms calc(var(--index) * 100ms)
    backwards;
`;

const DualListItem = styled(ListItem)`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Value = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

const Box = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
`;

const Label = styled.span`
  color: var(--color-gray-700);
  font-size: 0.875rem;

  ${DualListItem} & {
    font-size: 0.75rem;
  }
`;

const Mouse = styled.span`
  @media (hover: none) {
    display: none;
  }
`;

const Touch = styled.span`
  @media (hover: hover) {
    display: none;
  }
`;

export default ColorTable;
